import { z } from "zod/v4";

import { ConfigurationResponseDtoSchema } from "./configurations";
import { BaseIntegrationDtoSchema } from "./integrations";
import { SubscriptionStatusList } from "./organization-subscriptions";
import { ProcessStatusTableDtoSchema } from "./processes";
import { ActiveStripeSubscriptionDtoSchema } from "./stripe-subscription-links";
import { UserDtoSchema } from "./users";

export const IntegrationStatusList = [
  "active",
  "stale",
  "not_integrated",
  "completed_typeform",
  "collab_access_verified",
  "integration_scheduled",
  "integration_complete",
] as const;
export type IntegrationStatus = (typeof IntegrationStatusList)[number];

export const OrgAuthDtoSchema = z.object({
  accessToken: z.string(),
  shopId: z.string(),
});

export type OrgAuthDto = z.infer<typeof OrgAuthDtoSchema>;

export const OrgShopCurrencyDtoSchema = z.object({
  locale: z.string().default("en"),
  code: z.string().default("USD"),
});

export type OrgShopCurrencyDto = z.infer<typeof OrgShopCurrencyDtoSchema>;

export const FullOrganizationDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  apiKey: z.string().nullish(),
  lastUpdateTs: z.number(),
  createdAtTs: z.number(),
  accessToken: z.string().nullish(),
  shopId: z.string().nullish(),
  integrationStatus: z.enum(IntegrationStatusList).default("not_integrated"),
  customerEmail: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  scopes: z.string().array().nullish().default([]),
  domain: z.string().nullish(),
  integrations: BaseIntegrationDtoSchema.array().default([]),
  subscriptionId: z.uuid().nullish(),
  subscriptionStatus: z.enum(SubscriptionStatusList),
  activeSubscriptions: ActiveStripeSubscriptionDtoSchema.array().default([]),
  alerts: ProcessStatusTableDtoSchema.array().default([]),
  freeTrialExpiresAtTs: z.number().nullish(),
  configurations: ConfigurationResponseDtoSchema.array(),
  orderCount: z.number().nullish(),
  timezone: z.string().nullish(),
  installed: z.boolean(),
});

export const OrganizationDtoSchema = FullOrganizationDtoSchema.omit({
  apiKey: true,
  accessToken: true,
});
export type OrganizationDto = z.infer<typeof OrganizationDtoSchema>;

export const CreateOrganizationDtoSchema = OrganizationDtoSchema.omit({
  lastUpdateTs: true,
  createdAtTs: true,
});

export const OrganizationWithAuthDtoSchema = OrganizationDtoSchema.extend({
  shopId: z.string(),
  accessToken: z.string(),
});

export type OrganizationWithAuthDto = z.infer<
  typeof OrganizationWithAuthDtoSchema
>;

export const InternalOrganizationDtoSchema = OrganizationDtoSchema.extend({
  users: UserDtoSchema.array(),
  numCreatedExperiences: z.number().nullish(),
  numStartedExperiences: z.number().nullish(),
}).omit({
  configurations: true,
});

export type InternalOrganizationDto = z.infer<
  typeof InternalOrganizationDtoSchema
>;

export type OrganizationShopIdName = { shopId: string; name: string };

export type IdName = { id: string; name: string };

export type ShopGlobalMetafield = {
  functionsIncludeSubscriptionProducts: boolean;
};
